const roundScoreValue = (value: number): number =>
  Math.round(value * 10) / 10;

export const formatAssessmentScoreNumber = (value: number): string => {
  const rounded = roundScoreValue(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

// Base score excluding bonus (`score` is total = base + bonus).
// Prefer explicit `user_score` / `gamification_bonus_score` when available.
export const getAssessmentBaseScore = (
  score: number,
  bonusScore: number = 0,
): number => roundScoreValue(Math.max(0, score - bonusScore));

export const formatAssessmentBonusSuffix = (bonusScore: number = 0): string =>
  bonusScore > 0
    ? ` (+ ${formatAssessmentScoreNumber(bonusScore)} bonus)`
    : "";

// Display format: `9/10 (+ 4 bonus)` when bonus > 0, otherwise `9/10`.
// `score` is total (base + bonus); `maxScore` is max without bonus.
export const formatAssessmentScore = (
  score: number,
  maxScore: number,
  bonusScore: number = 0,
): string => {
  const base = getAssessmentBaseScore(score, bonusScore);
  const main =
    maxScore > 0
      ? `${formatAssessmentScoreNumber(base)}/${formatAssessmentScoreNumber(maxScore)}`
      : formatAssessmentScoreNumber(base);
  return `${main}${formatAssessmentBonusSuffix(bonusScore)}`;
};

// Points-only display: `25 pts (+ 2 bonus)`.
export const formatAssessmentPoints = (
  score: number,
  bonusScore: number = 0,
): string => {
  const base = getAssessmentBaseScore(score, bonusScore);
  return `${formatAssessmentScoreNumber(base)} pts${formatAssessmentBonusSuffix(bonusScore)}`;
};

// Contest ranking score already excludes gamification — do not subtract.
export const formatAssessmentUserScorePoints = (
  userScore: number,
  gamificationBonusScore: number = 0,
): string =>
  `${formatAssessmentScoreNumber(userScore)} pts${formatAssessmentBonusSuffix(
    gamificationBonusScore,
  )}`;

export const formatAssessmentScoreFraction = (
  score: number,
  maxScore: number,
): string =>
  maxScore > 0
    ? `${formatAssessmentScoreNumber(score)}/${formatAssessmentScoreNumber(maxScore)}`
    : formatAssessmentScoreNumber(score);

// After-answer feedback: `(+5 Points) (+ 5 bonus)`.
// Per-answer `score` already excludes gamification — do not subtract bonus.
export const formatAssessmentScoreFeedback = (
  score: number,
  bonusScore: number = 0,
): string => {
  const sign = score > 0 ? "+" : "";
  return `(${sign}${formatAssessmentScoreNumber(score)} Points)${formatAssessmentBonusSuffix(bonusScore)}`;
};
